/* global localStorage, sessionStorage, IDBDatabase, IDBValidKey */
/**
 * State Snapshot - Capture full app state for debugging and storytelling
 *
 * Usage:
 *   const snapshot = await window.captureStateSnapshot("Step 1: After begin");
 *   console.log(snapshot);
 *   // Export to file or clipboard
 */

export interface StateSnapshot {
  timestamp: string;
  label?: string;
  engineState: string | null;
  currentSession: any;
  localStorage: Record<string, string>;
  sessionStorage: Record<string, string>;
  indexedDB: Record<string, any>;
  consoleLogs?: Array<{ level: string; args: any[] }>;
}

export async function captureStateSnapshot(
  label?: string,
): Promise<StateSnapshot> {
  const manager = window.__retreatManager as any;

  const snapshot: StateSnapshot = {
    timestamp: new Date().toISOString(),
    label,
    engineState: manager?.getState?.() || null,
    currentSession: manager?.getCurrentSession?.() || null,
    localStorage: { ...localStorage },
    sessionStorage: { ...sessionStorage },
    indexedDB: {},
  };

  // Read IndexedDB (with timeout to prevent hanging)
  try {
    const dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      const timeout = setTimeout(() => resolve(null), 2000);
      const req = indexedDB.open("retreat-conductor");
      req.onsuccess = () => {
        clearTimeout(timeout);
        resolve(req.result);
      };
      req.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };
      req.onblocked = () => {
        clearTimeout(timeout);
        resolve(null);
      };
    });

    const db = await dbPromise;
    if (!db) {
      snapshot.indexedDB = {};
      return snapshot;
    }

    try {
      const tx = db.transaction(["sessions"], "readonly");
      const store = tx.objectStore("sessions");
      const allKeys = await new Promise<IDBValidKey[]>((resolve) => {
        const req = store.getAllKeys();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve([]);
      });

      snapshot.indexedDB = {};
      for (const key of allKeys) {
        const data = await new Promise<any>((resolve) => {
          const req = store.get(key);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(null);
        });
        if (data) snapshot.indexedDB[String(key)] = data;
      }
    } catch {
      snapshot.indexedDB = {};
    }

    db.close();
  } catch {
    snapshot.indexedDB = {};
  }

  return snapshot;
}

export function formatSnapshot(snapshot: StateSnapshot): string {
  return `
═══════════════════════════════════════════════════════════════
${snapshot.label ? ` ${snapshot.label}` : ""}
${snapshot.timestamp}
═══════════════════════════════════════════════════════════════

ENGINE STATE: ${snapshot.engineState}
SESSION: ${snapshot.currentSession ? "✓ Active" : "✗ None"}

STORAGE:
  localStorage: ${Object.keys(snapshot.localStorage).length} keys
  sessionStorage: ${Object.keys(snapshot.sessionStorage).length} keys
  indexedDB: ${Object.keys(snapshot.indexedDB).length} keys

${
  snapshot.consoleLogs && snapshot.consoleLogs.length > 0
    ? `CONSOLE LOGS (last ${snapshot.consoleLogs.length}):
${snapshot.consoleLogs.map((log) => `  [${log.level}] ${JSON.stringify(log.args)}`).join("\n")}

`
    : ""
}
FULL STATE:
${JSON.stringify(snapshot, null, 2)}
═══════════════════════════════════════════════════════════════
`;
}

export function exportSnapshots(snapshots: StateSnapshot[]): string {
  return snapshots.map((s) => formatSnapshot(s)).join("\n");
}
